import api from './api';

/**
 * Role Service
 * Handles role CRUD operations via the /api/roles endpoints
 */
const roleService = {
  /**
   * Get all roles with optional filtering
   * @param {string} roleType - Filter by 'system' or 'project'
   * @param {string} search - Search by name or description
   * @param {number} page
   * @param {number} limit
   * @returns {Promise<Object>} - { roles, total, page, limit, total_pages }
   */
  getAllRoles: async (roleType = null, search = null, page = 1, limit = 100) => {
    try {
      const params = new URLSearchParams();
      if (roleType) params.append('role_type', roleType);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', limit);

      const response = await api.get(`/roles/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error.response?.data?.detail || 'Failed to fetch roles';
    }
  },

  /**
   * Get a role by ID (role_id slug or ObjectId)
   * @param {string} roleId
   * @returns {Promise<Object>}
   */
  getRoleById: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching role ${roleId}:`, error);
      throw error.response?.data?.detail || 'Failed to fetch role';
    }
  },

  /**
   * Create a new role
   * @param {Object} roleData - { name, role_type, description, permissions }
   * @returns {Promise<Object>}
   */
  createRole: async (roleData) => {
    try {
      const response = await api.post('/roles/', roleData);
      return response.data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error.response?.data?.detail || 'Failed to create role';
    }
  },

  /**
   * Update a role
   * @param {string} roleId
   * @param {Object} roleData - { name, description, permissions }
   * @returns {Promise<Object>}
   */
  updateRole: async (roleId, roleData) => {
    try {
      const response = await api.patch(`/roles/${roleId}/`, roleData);
      return response.data;
    } catch (error) {
      console.error(`Error updating role ${roleId}:`, error);
      throw error.response?.data?.detail || 'Failed to update role';
    }
  },

  /**
   * Delete a role
   * @param {string} roleId
   * @param {boolean} force - Force delete even if users are assigned
   * @returns {Promise<Object>}
   */
  deleteRole: async (roleId, force = false) => {
    try {
      const response = await api.delete(`/roles/${roleId}/?force=${force}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting role ${roleId}:`, error);
      let msg = error.data?.detail || error.message || 'Failed to delete role';
      if (msg.includes('Use force=true')) {
        msg = msg.replace('Use force=true to delete anyway.', 'Please update the roles of those users first.');
      }
      throw msg;
    }
  },

  /**
   * Set permissions for a role
   * @param {string} roleId
   * @param {Array<string>} permissions - Array of permission path strings
   * @returns {Promise<Object>}
   */
  assignPermissions: async (roleId, permissions) => {
    try {
      const response = await api.put(`/roles/${roleId}/permissions/`, { permissions });
      return response.data;
    } catch (error) {
      console.error(`Error assigning permissions to role ${roleId}:`, error);
      throw error.response?.data?.detail || 'Failed to assign permissions';
    }
  },

  /**
   * Get users assigned to a role
   * @param {string} roleId
   * @returns {Promise<Object>}
   */
  getRoleUsers: async (roleId) => {
    try {
      const response = await api.get(`/roles/${roleId}/users/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching users for role ${roleId}:`, error);
      throw error.response?.data?.detail || 'Failed to fetch role users';
    }
  },
};

export default roleService;
